import {findToolPartByTrackingId} from './toolPartSelectors';

const INITIAL_STATE = {
  operations: {},
  arts: {},
  invalidatedOperations: {},
  connectionStatus: 'disconnected',
  lastInvalidationRevision: null,
};

function operationKey(sessionId, trackingId) {
  return `${sessionId}:${trackingId}`;
}

function isTerminalStatus(status) {
  return status === 'completed' || status === 'error';
}

function isTerminalToolOutputStatus(status) {
  return status === 'completed' || status === 'error';
}

function revisionReached(snapshotRevision, invalidatedRevision) {
  if (snapshotRevision == null || invalidatedRevision == null) {
    return false;
  }

  const snapshotNumber = Number(snapshotRevision);
  const invalidatedNumber = Number(invalidatedRevision);
  if (Number.isFinite(snapshotNumber) && Number.isFinite(invalidatedNumber)) {
    return snapshotNumber >= invalidatedNumber;
  }

  return String(snapshotRevision) >= String(invalidatedRevision);
}

function filterOverlayBySession(entries, sessionId) {
  if (!sessionId) {
    return entries;
  }

  return Object.fromEntries(
    Object.entries(entries).filter(([key, value]) => {
      if (value?.sessionId === sessionId) {
        return true;
      }
      return key.startsWith(`${sessionId}:`);
    }),
  );
}

function markOperationInvalidated(state, sessionId, trackingId, revision) {
  const invalidatedOperations = {...state.invalidatedOperations};
  const invalidationMark = revision ?? Date.now();

  if (trackingId) {
    invalidatedOperations[operationKey(sessionId, trackingId)] = invalidationMark;
    return invalidatedOperations;
  }

  Object.keys(state.operations).forEach((key) => {
    if (key.startsWith(`${sessionId}:`)) {
      invalidatedOperations[key] = invalidationMark;
    }
  });

  return invalidatedOperations;
}

function isOperationInvalidated(state, key) {
  return Object.prototype.hasOwnProperty.call(state.invalidatedOperations, key);
}

/**
 * Pure reducer for versioned campaign_live_update socket events.
 */
export function reduceCampaignLiveOverlay(state, action) {
  if (action?.type === 'reset') {
    return createInitialLiveOverlay();
  }

  const event = action?.event ?? action;
  const activeSessionId = action?.activeSessionId;
  if (!event?.agentSessionId) {
    return state;
  }
  if (activeSessionId && event.agentSessionId !== activeSessionId) {
    return state;
  }

  if (event.type === 'operation_progress') {
    const trackingId = event.trackingId;
    if (!trackingId) {
      return state;
    }
    const key = operationKey(event.agentSessionId, trackingId);

    if (isOperationInvalidated(state, key)) {
      return state;
    }

    const prev = state.operations[key];
    const incomingStep = event.payload?.currentStep ?? 0;
    const prevStep = prev?.currentStep ?? -1;

    if (prev && isTerminalStatus(prev.status)) {
      return state;
    }
    if (prev && incomingStep < prevStep) {
      return state;
    }

    return {
      ...state,
      operations: {
        ...state.operations,
        [key]: {
          ...prev,
          sessionId: event.agentSessionId,
          trackingId,
          ...event.payload,
        },
      },
    };
  }

  if (event.type === 'art_upsert') {
    const pieceNumber = event.payload?.pieceNumber;
    if (pieceNumber == null) {
      return state;
    }
    const artKey = `${event.agentSessionId}:${pieceNumber}`;
    return {
      ...state,
      arts: {
        ...state.arts,
        [artKey]: {
          sessionId: event.agentSessionId,
          pieceNumber,
          trackingId: event.trackingId ?? event.payload?.trackingId ?? null,
          ...event.payload,
        },
      },
    };
  }

  if (event.type === 'snapshot_invalidated') {
    const trackingId = event.trackingId;
    const invalidatedOperations = markOperationInvalidated(
      state,
      event.agentSessionId,
      trackingId,
      event.revision ?? null,
    );

    let operations = {...state.operations};
    if (trackingId) {
      const key = operationKey(event.agentSessionId, trackingId);
      const {[key]: _omit, ...restOps} = operations;
      void _omit;
      operations = restOps;
    } else {
      operations = Object.fromEntries(
        Object.entries(operations).filter(
          ([key]) => !key.startsWith(`${event.agentSessionId}:`),
        ),
      );
    }

    return {
      ...state,
      operations,
      invalidatedOperations,
      lastInvalidationRevision: event.revision ?? state.lastInvalidationRevision,
    };
  }

  return state;
}

export function createInitialLiveOverlay() {
  return {
    ...INITIAL_STATE,
    operations: {},
    arts: {},
    invalidatedOperations: {},
  };
}

/**
 * Reconciles live overlay against transcript terminality and snapshot revision.
 * Does not discard overlay solely because snapshot contains the same operation.
 */
export function reconcileLiveOverlay(overlay, {snapshot = null, messages = []} = {}) {
  if (!overlay) {
    return overlay;
  }

  const sessionId = snapshot?.sessionId ?? null;
  let operations = filterOverlayBySession(overlay.operations ?? {}, sessionId);
  const arts = filterOverlayBySession(overlay.arts ?? {}, sessionId);

  operations = Object.fromEntries(
    Object.entries(operations).filter(([key, op]) => {
      const trackingId = op.trackingId;
      if (!trackingId) {
        return true;
      }

      const part = findToolPartByTrackingId(messages, trackingId);
      if (part && isTerminalToolOutputStatus(part.output?.status)) {
        return false;
      }

      const invalidatedRevision = overlay.invalidatedOperations?.[key];
      if (
        invalidatedRevision != null &&
        revisionReached(snapshot?.revision, invalidatedRevision)
      ) {
        return false;
      }

      if (op.status && isTerminalStatus(op.status)) {
        return false;
      }

      return true;
    }),
  );

  return {
    ...overlay,
    operations,
    arts,
  };
}

export {operationKey};
